import { useCallback, useRef } from "react";
import { IconPaperclip } from "@/assets/icons/geist/IconPaperclip";

import { useToast } from "@/components/podkit/toasts/use-toast";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/flexkit/Button";
import { type ImageAttachment } from "@/components/ai/prompt-input/state";
import { processImageFiles } from "@/components/ai/prompt-input/use-image-drop";

export type { ImageAttachment };

const ALLOWED_TYPES = ["image/png", "image/jpeg"];

type AddImageButtonProps = {
    onImagesSelected: (images: ImageAttachment[]) => void;
    disabled?: boolean;
    className?: string;
};

/**
 * Button component for adding images - renders just the paperclip icon
 */
export function AddImageButton({ onImagesSelected, disabled, className }: AddImageButtonProps) {
    const { toast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFiles = useCallback(
        async (files: FileList | null) => {
            const images = await processImageFiles(files, toast);
            if (images.length > 0) {
                onImagesSelected(images);
            }
        },
        [onImagesSelected, toast],
    );

    const handleClick = useCallback(() => {
        inputRef.current?.click();
    }, []);

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={ALLOWED_TYPES.join(",")}
                multiple
                onChange={(e) => {
                    void handleFiles(e.target.files);
                    // Reset input so same file can be selected again
                    e.target.value = "";
                }}
                className="hidden"
                disabled={disabled}
            />
            <Tooltip content="Attach image">
                <Button
                    variant="ghost"
                    size="sm"
                    LeadingIcon={IconPaperclip}
                    onClick={handleClick}
                    disabled={disabled}
                    className={cn("text-content-secondary", className)}
                    aria-label="Attach image"
                    data-tracking-id="add-image-prompt-input"
                />
            </Tooltip>
        </>
    );
}
